import React, {
  memo,
  Fragment,
  useState,
  useCallback,
}                            from 'react';
import styled                from 'styled-components';
import {
  Icon,
  // Collapse,
  TableRow,
  TableBody,
  TableCell,
}                            from '@material-ui/core';
import KeyboardArrowUpIcon   from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';

import {
  TDataTableRow,
  IDataTableCell,
}                            from '@typings/components';

const SectionTableRow = styled(TableRow)`
  && {
    display: flex;

    &:not(:first-child){
      margin-top: 15px;
    }

    h2 {
      white-space   : nowrap;
      margin-left   : 5px;
      margin-bottom : 15px;
    }

    svg {
      margin : 2px 0 0 5px;
      color  : ${({ theme: { textColor } }): string => textColor.secondary};
    }
  }
` as any;

const StyledTableRow = styled(TableRow)`
  && {
    ${({ rowheight }: any): false | string => (rowheight && `height: ${rowheight}`)};

    background-color: ${({ theme: { backgroundColor }, index }: any): string => (
    index % 2
      ? backgroundColor.lightBlue
      : backgroundColor.white)};

    ${({ aligntop }: any): false | string => (aligntop && `
      vertical-align: top;

      td {
        padding-top: 10px;
      }
    `)};

    select {
      padding-top    : 8px;
      padding-bottom : 8px;
    }

    .MuiButton-sizeSmall {
      margin    : 0;
      font-size : 14px;
      min-width : 60px;
      padding   : 0 5px;
    }
  }
` as any;

const LinkTableCell = styled(TableCell)`
  span {
    color           : ${({ theme: { textColor } }): string => textColor.secondary};
    text-decoration : underline;
    cursor          : pointer;
  }
`;

interface ISectionRowProps {
  title       : string;
  onClick?    : (meta: string) => void;
  collapsed   : boolean;
  collapsable : boolean;
}

const DataTableSectionRow: React.FC<ISectionRowProps> = memo(({
  title,
  onClick,
  collapsed,
  collapsable,
}) => (
  <SectionTableRow onClick={collapsable ? onClick : undefined}>
    <h2>{title}</h2>
    {collapsable && (
      <Icon>
        {collapsed
          ? <KeyboardArrowDownIcon />
          : <KeyboardArrowUpIcon />}
      </Icon>
    )}
  </SectionTableRow>
));

interface IDataRowProps {
  row        : TDataTableRow;
  rowIndex   : number;
  aligntop?  : boolean;
  rowheight? : string;
  handlePush : (link:string) => void;
}

const DataTableDataRow: React.FC<IDataRowProps> = memo(({
  row,
  rowIndex,
  aligntop,
  rowheight,
  handlePush,
}: IDataRowProps): JSX.Element => (
  <StyledTableRow
    index     = {rowIndex}
    aligntop  = {aligntop}
    rowheight = {rowheight}
  >
    {row.filter((cell) => !cell.meta).map(
      (cell: IDataTableCell, cellIndex: number): JSX.Element => (
        <Fragment key={cellIndex}>
          {cell.link ? (
            <LinkTableCell
              align   = {cell.align ?? 'left'}
              onClick = {(): void => handlePush(cell.link ?? '')}
            >
              <span>{cell.value}</span>
            </LinkTableCell>
          ) : (
            <TableCell align={cell.align ?? 'left'}>
              {cell.value}
            </TableCell>
          )}
        </Fragment>
      ),
    )}
  </StyledTableRow>
));

interface ITableBodyProps {
  rows         : TDataTableRow[];
  aligntop?    : boolean;
  rowheight?   : string;
  collapsed?   : boolean;
  collapsable? : boolean;
  handlePush   : (link: string) => void;
}

export const DataTableBody: React.FC<ITableBodyProps> = memo(({
  rows,
  aligntop,
  rowheight,
  collapsed,
  collapsable,
  handlePush,
}: ITableBodyProps): JSX.Element => {
  const [collapse, setCollapse] = useState<{[key: string]: boolean}>({});

  const handleClickSection = useCallback(
    (value: string): void => {
      setCollapse({
        ...collapse,
        [value]: !collapse[value],
      });
    },
    [collapse],
  );

  return (
    <TableBody>
      {rows.map(
        (row: TDataTableRow, rowIndex: number): JSX.Element => {
          const isSection    = row[0].section;
          const sectionMeta  = `${row[0].value}`;
          const sectionTitle = `${row[0].title}`;
          const isCollapsed  = !!collapse[row[0].value];

          if (
            collapsable
            && isSection
            && collapse[sectionMeta] === undefined
          ) {
            setCollapse({
              ...collapse,
              [sectionMeta]: !!collapsed,
            });
          }

          return (
            <Fragment key={rowIndex}>
              {isSection
                ? (
                  <DataTableSectionRow
                    title       = {sectionTitle}
                    onClick     = {(): void => handleClickSection(sectionMeta)}
                    collapsed   = {isCollapsed}
                    collapsable = {!!collapsable}
                  />
                ) : (
                  <>
                    {!isCollapsed && (
                      <DataTableDataRow
                        row        = {row}
                        rowIndex   = {rowIndex}
                        aligntop   = {aligntop}
                        rowheight  = {rowheight}
                        handlePush = {handlePush}
                      />
                    )}
                  </>
                )}
            </Fragment>
          );
        },
      )}
    </TableBody>
  );
});
